import { BrushStroke } from "@/core/utils/brushing"
import { AiModel } from "@/store/editor/healing-state"
import { vec2 } from "gl-matrix"

export enum OutputColorSpace {
	sRGB = 0,
	displayP3 = 1,
	gamma = 2,
}

export enum LookTableType {
	unknown = 0,
	hsv = 1,
	rgb = 2,
}

export enum LookTableVersion {
	version1 = 1,
	version2 = 2,
}

export enum LookTableEncoding {
	linear = 0,
	sRGB = 1,
}

export enum LookTableRGBPrimaries {
	sRGB = 0,
	adobe,
	proPhoto,
	p3,
	rec2020,
	invalid,
}

export enum LookTableRGBGamma {
	linear = 0,
	sRGB,
	gamma1_8,
	gamma2_2,
	rec2020,
	invalid,
}

export interface LookTable {
	id: string
	type: LookTableType
	version: LookTableVersion
	encoding: LookTableEncoding
	hueDivisions: number // >= 1
	satDivisions: number // >= 2
	valDivisions: number // >= 1
	data: Float32Array
	minAmount: number
	maxAmount: number
	rgbPrimaries: LookTableRGBPrimaries
	rgbGamma: LookTableRGBGamma
}

export enum ColorProfileName {
	Standard = 0,
	StandardMonochrome = 1,
	Color = 2,
	Monochrome = 3,
	Embedded = 4,
}

export const emptyPointColor: PointColorHSL = {
	id: "noop",
	color: [0, 0, 0],
	hueShift: 0,
	satShift: 0,
	lumShift: 0,
	range: 0,
	hueRange: [0.0, 0.0, 0.0, 0.0],
	satRange: [0.0, 0.0, 0.0, 0.0],
	lumRange: [0.0, 0.0, 0.0, 0.0],
}

export interface PointColorHSL {
	id: string
	color: [number, number, number]
	hueShift: number
	satShift: number
	lumShift: number
	range: number
	hueRange: [number, number, number, number]
	satRange: [number, number, number, number]
	lumRange: [number, number, number, number]
}

export interface Adjustments {
	// Color profile
	colorProfile: ColorProfileName | null

	// Lens correction settings information
	enableLensCorrections: boolean | null
	correctLensDistortion: boolean
	lensDistortionCorrectionFactor: number
	correctLensVignette: boolean
	lensVignetteCorrectionFactor: number
	correctLensChromaticAberration: boolean

	// White balance (optional)
	// If unset, use as-shot temp and tint
	temperature?: number
	tint?: number

	// Global
	exposure: number
	shadows: number
	highlights: number
	whites: number
	blacks: number
	contrast: number
	saturation: number
	vibrance: number

	vignette: number
	vignetteFeather: number
	vignetteMidpoint: number
	vignetteRoundness: number
	vignetteHighlights: number

	texture: number
	clarity: number
	dehaze: number

	// Point curves
	curvesAll: Curve
	curvesRed: Curve
	curvesGreen: Curve
	curvesBlue: Curve

	// HSL
	hueRed: number
	saturationRed: number
	luminanceRed: number
	hueOrange: number
	saturationOrange: number
	luminanceOrange: number
	hueYellow: number
	saturationYellow: number
	luminanceYellow: number
	hueGreen: number
	saturationGreen: number
	luminanceGreen: number
	hueAqua: number
	saturationAqua: number
	luminanceAqua: number
	hueBlue: number
	saturationBlue: number
	luminanceBlue: number
	huePurple: number
	saturationPurple: number
	luminancePurple: number
	hueMagenta: number
	saturationMagenta: number
	luminanceMagenta: number

	// Point color
	pointColors: PointColorHSL[]

	// Calibration
	calibrationRedHue: number
	calibrationRedSat: number
	calibrationGreenHue: number
	calibrationGreenSat: number
	calibrationBlueHue: number
	calibrationBlueSat: number

	// Parametric curves
	parametricDarksStop: number
	parametricMidsStop: number
	parametricLightsStop: number
	parametricHighlights: number
	parametricLights: number
	parametricDarks: number
	parametricShadows: number

	// Color grading
	gradingShadowsHue: number
	gradingShadowsSaturation: number
	gradingShadowsLuminance: number
	gradingMidtonesHue: number
	gradingMidtonesSaturation: number
	gradingMidtonesLuminance: number
	gradingHighlightsHue: number
	gradingHighlightsSaturation: number
	gradingHighlightsLuminance: number
	gradingGlobalHue: number
	gradingGlobalSaturation: number
	gradingGlobalLuminance: number
	gradingBlending: number
	gradingBalance: number

	// Detail
	luminanceNoiseReduction: number
	colorNoiseReduction: number
	sharpen: number

	// Crop
	cropStraighten: number
	crop: Crop
	cropRatio: CropRatio

	// Rotation and flip
	orientation: number
	flipX: boolean
	flipY: boolean

	// Transform & Perspective
	lensDistortion: number
	perspectiveVertical: number
	perspectiveHorizontal: number
	perspectiveRotate: number
	perspectiveScale: number
	perspectiveAspect: number
	perspectiveOffsetX: number
	perspectiveOffsetY: number

	// Grain
	grainAmount: number
	grainSize: number
	grainRoughness: number

	// Masks
	localAdjustments?: MaskGroup[]

	// Healing
	healingMasks?: HealingItem[]

	// Defringe
	defringePurpleHueRange: [number, number]
	defringePurpleAmount: number
	defringeGreenHueRange: [number, number]
	defringeGreenAmount: number

	// User LUT
	userLutId?: string
	userLutStrength: number
	userLutTitle?: string // Store the name of CUBE or XMP file

	// Color Match
	colorMatchImageId?: string | null
	colorMatchStrength: number
	colorMatchSkinToneOptimized: boolean

	// Yuhe: only used for rendering input image for AI color match parameter estimation
	disableProfileLookTable?: boolean
}

export interface MaskGroupAdjustments {
	temperature: number
	tint: number
	saturation: number
	toningHue: number
	toningSaturation: number
	exposure: number
	contrast: number
	highlights: number
	shadows: number
	whites: number
	blacks: number
	clarity: number
	texture: number
	dehaze: number
	sharpen: number
	pointColors: PointColorHSL[]
	strength: number
}

export interface MaskGroup extends MaskGroupAdjustments {
	id: string
	name: string
	active: boolean
	masks: Mask[]
	retouching?: {
		control: string
		skinTone?: {
			toneId: string
			strength: number
		}
	}
}

export type MaskBlendMode = "add" | "subtract"

export type MaskType = "subject" | "radial" | "gradient" | "sky" | "background" | "people" | "brush"

export type Mask =
	| RadialMask
	| GradientMask
	| SubjectMask
	| SkyMask
	| BackgroundMask
	| PeopleMask
	| BrushMask

export interface BaseMask {
	id: string
	name: string
	active: boolean
	inverted: boolean
	blendMode: MaskBlendMode
}

export interface RadialMask extends BaseMask {
	type: "radial"
	centerX: number
	centerY: number
	radiusX: number
	radiusY: number
	angle: number
	feather: number
}

export interface GradientMask extends BaseMask {
	type: "gradient"
	startX: number
	startY: number
	endX: number
	endY: number
}

export interface SubjectMask extends BaseMask {
	type: "subject"
}

export interface BackgroundMask extends BaseMask {
	type: "background"
}

export interface PeopleMask extends BaseMask {
	type: "people"
	personIndex?: number
	personIndexes?: number[]
	peoplePart?: PeopleMaskPart
}

export type PeopleMaskPart =
	| "entire"
	| "facialSkin"
	| "eyebrows"
	| "eyes"
	| "lips"
	| "clothing"
	| "bodySkin"
	| "hair"

export interface SkyMask extends BaseMask {
	type: "sky"
}

export interface BrushMask extends BaseMask {
	type: "brush"
	strokes: BrushStroke[]
}

export interface Rectangle {
	x: number
	y: number
	width: number
	height: number
}

export type AiVariantPromptStrategy = "prompt_enhanced" | "default_prompt" | "standard" | "unknown"

export interface HealingAiInputNormalization {
	effectiveExposure: number
}

export interface HealingGeneratedSource {
	mimeType: string
	data: string // base64 encoded full image data (un-oriented space)
	width: number // full image width
	height: number // full image height
	cropRect: Rectangle // crop region in un-oriented space (where to crop from full image)
	fullRect: Rectangle // destination rect in image space (where this healing applies)
	prompt?: string // prompt used to generate this AI healing (user input; what the textarea shows on re-select)
	enhancedPrompt?: string // server-side AI-rewritten prompt actually fed into diffusion (debug-only display)
	aiInputNormalization?: HealingAiInputNormalization // Exposure/tonemap normalization used for QWEN input and inverse conversion
	alignment?: {
		// Alignment correction in un-oriented space (median translation from optical flow)
		dx: number // horizontal offset in pixels
		dy: number // vertical offset in pixels
		scaleX?: number // small anisotropic scale around 1.0 (e.g., 0.95 - 1.05)
		scaleY?: number // small anisotropic scale around 1.0 (e.g., 0.95 - 1.05)
		confidence: number // 0-1, based on MAD
	}
	orientation: number // orientation of the image when the healing was generated
	aiModel: AiModel
	promptStrategy?: AiVariantPromptStrategy
}

export type HealingMode = "healing" | "ai-generate" | "ai-erase"

export interface HealingItem {
	id: string
	strokes: BrushStroke[]
	strokesRect: Rectangle // original bounding box of brush strokes, no padding or clamping
	sourceRect: Rectangle // source rect clamped to image bounds and of same size as destinationRect
	destinationRect: Rectangle // destination rect clamped to image bounds and of same size as sourceRect
	sourceCenter: vec2 // stroke center, not rect center
	destinationCenter: vec2 // stroke center, not rect center
	aiVariations?: HealingGeneratedSource[] // Multiple AI-generated variations to choose from
	selectedVariation?: number // Index of currently selected variation (0-based)
	applyBrushMask?: boolean // Whether to use brush strokes as mask (true) or full rectangle mask (false). Default: true
	applyAlignment?: boolean // Whether to apply optical flow alignment correction. Default: true
	mode?: HealingMode // Whether to use healing or AI generating or erasing mode. Default: healing
}

export type MaskDetectionState = "started" | "succeeded" | "canceled" | "failed"

export type AdjustmentKey = keyof Adjustments
export type MaskAdjustmentKey = keyof MaskGroupAdjustments
export type MaskGroupKey = keyof MaskGroup
export type AdjustmentRange = [min: number, max: number]
export type Curve = [number, number][]
export type Crop = [number, number, number, number]
export type CropRatio = [number, number]
export type ViewMode = "before" | "after" | "compare" | "crop"

export interface EditorEvent {
	type: "render" | "histogram"
}

export type RenderColorSpace = PredefinedColorSpace | "gamma"
